import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserDetailsDto } from '../../../services/User/userService.dto';
import { userService } from '../../../services/User/userService';
import { useError } from '../../../providers/useError';
import { authService } from '../../../services/Auth/authService';
import { AuctionDto } from '../../../services/Auction/auctionService.dto';
import { isAuctionInProgress } from '../utils/auctionUtils';
import { useAuctionEndDetection } from './useAuctionEndDetection';

const FIRST_NAME_DEFAULT = 'On-Site Auction';
const LAST_NAME_DEFAULT = 'Participant';
const AUTH_TOKEN_TTL_IN_SECS = 60 * 60 * 6;

export type TechnicalUserStatus = 'INITIALIZING' | 'NOT_CREATED' | 'INITIALIZED' | 'NOT_FOUND' | 'UNKNOWN_ERROR';

export const useTechnicalUser = (auction?: AuctionDto) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [technicalUserStatus, setTechnicalUserStatus] = useState<TechnicalUserStatus>('INITIALIZING');
  const [technicalUser, setTechnicalUser] = useState<UserDetailsDto>();

  const { errorMessage } = useError();
  const { t } = useTranslation('onsite');

  useEffect(() => {
    init();
  }, [auction?.id]);

  useEffect(() => {
    updateTechnicalUser();
  }, [firstName, lastName]);

  const init = async () => {
    if (!auction) return;

    const technicalUser = await fetchTechnicalUser();
    if (technicalUser) {
      setFirstName(technicalUser.firstName);
      setLastName(technicalUser.lastName);
      setTechnicalUser(technicalUser);
      setTechnicalUserStatus('INITIALIZED');
      return technicalUser;
    } else if (isAuctionInProgress(auction)) {
      setFirstName(FIRST_NAME_DEFAULT);
      setLastName(LAST_NAME_DEFAULT);
      setTechnicalUserStatus('NOT_CREATED');
    } else {
      setTechnicalUserStatus('NOT_FOUND');
    }
  };

  const resolveTechnicalUser = async () => {
    if (technicalUserStatus === 'INITIALIZED') {
      return technicalUser;
    } else if (technicalUserStatus === 'NOT_CREATED') {
      const technicalUser = await createTechnicalUser();
      if (technicalUser) {
        setFirstName(technicalUser.firstName);
        setLastName(technicalUser.lastName);
        setTechnicalUser(technicalUser);
        setTechnicalUserStatus('INITIALIZED');
        return technicalUser;
      }
    } else {
      return await init();
    }
  };

  useAuctionEndDetection(auction, init);

  const resolveAuthToken = async () => {
    if (authToken) {
      return authToken;
    }

    const technicalUser = await resolveTechnicalUser();
    if (!technicalUser) {
      return;
    }

    const newAuthToken = await loginTechnicalUser(technicalUser);

    if (newAuthToken) {
      setAuthToken(newAuthToken);
      return newAuthToken;
    }
  };

  const refetchTechnicalUser = async () => {
    const technicalUser = await fetchTechnicalUser();
    setTechnicalUser(technicalUser);
    setAuthToken('');
    return technicalUser!!;
  };

  const fetchTechnicalUser = async () => {
    if (auction) {
      try {
        const userResponse = await userService.fetchUsers({
          technicalAccountPurpose: 'ONSITE_AUCTION',
          relevantAuctionId: auction.id,
          page: 0,
          size: 1,
        });
        return userResponse?.data?.content?.[0];
      } catch (error) {
        setTechnicalUserStatus('UNKNOWN_ERROR');
        errorMessage(t('technical-user.cannot-fetch'));
      }
    }
  };

  const createTechnicalUser = async () => {
    if (auction) {
      try {
        const userResponse = await userService.createTechnicalUser({
          firstName,
          lastName,
          purpose: 'ONSITE_AUCTION',
          relevantAuctionId: auction.id,
        });

        return userResponse?.data;
      } catch (error) {
        setTechnicalUserStatus('UNKNOWN_ERROR');
        errorMessage(t('technical-user.cannot-create'));
      }
    }
  };

  const updateTechnicalUser = async () => {
    if (!!technicalUser && (technicalUser.firstName !== firstName || technicalUser.lastName !== lastName)) {
      try {
        const authToken = await resolveAuthToken();

        await userService.updateUser(
          {
            firstName,
            lastName,
            about: technicalUser.about,
            imageUrl: technicalUser.profileImageUrl,
          },
          authToken
        );

        setTechnicalUser({ ...technicalUser, firstName, lastName });
      } catch (error) {
        setFirstName(technicalUser?.firstName);
        setLastName(technicalUser?.lastName);
        errorMessage(t('technical-user.cannot-update'));
      }
    }
  };

  const loginTechnicalUser = async (technicalUser: UserDetailsDto) => {
    try {
      const loginResponse = await authService.loginUser(
        technicalUser.draftEmail || technicalUser.email,
        technicalUser.technicalAccountPassword!!,
        AUTH_TOKEN_TTL_IN_SECS
      );

      return loginResponse.data.authToken;
    } catch (error) {
      errorMessage(t('technical-user.cannot-login'));
    }
  };

  return {
    technicalUser,
    technicalUserStatus,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    resolveTechnicalUser,
    resolveAuthToken,
    refetchTechnicalUser,
  };
};
